'use client';

import type React from 'react';
import Image from 'next/image';

import { urlFor } from '@skybeam/shared-sanity';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { CardSlider } from '../../../_components/slider';
import { TextWithRegisteredSymbol } from '../../../_components/text-with-registered-symbol';
import { utils } from '../../../_components/utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
  SanityImage,
} from '../../../_components/utils/types';

type FeatureSliderCard = {
  content: string;
  icon?: SanityImage;
  title: string;
};

export type FeatureSliderSectionProps = {
  cards: FeatureSliderCard[];
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  heading: string;
};

export const FeatureSliderSection = ({
  cards,
  containerPadding,
  contentMaxWidth,
  heading,
}: FeatureSliderSectionProps) => {
  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);

  const sliderData = cards.map((card) => ({
    title: card.title,
    content: card.content,
    icon: card.icon?.asset ? (
      <Image
        src={urlFor(card.icon.asset).width(90).url()}
        alt={card.icon.alt ?? ''}
        width={45}
        height={45}
        className="tablet:size-9 laptop:size-[45px] size-3.5"
      />
    ) : null,
  }));

  return (
    <>
      {styleTags}
      <section
        className={cn(
          "tablet:px-6 tablet:pt-20 tablet:pb-24 desktop:gap-y-8 desktop:pl-14 desktop:pt-36 desktop:pb-28 laptop:py-32 laptop:pl-10 laptop:pr-0 flex flex-col gap-y-6 bg-[url('/tile-bg.svg')] bg-cover bg-top bg-no-repeat px-4 pt-11 pb-16 min-[1540px]:items-center",
          containerPaddingStyle.className,
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-y-6',
            contentMaxWidthStyle.className,
          )}
        >
          <h2 className="heading-marketing-2 laptop:text-left laptop:tracking-[-1.12px] desktop:tracking-[-1.28px] text-center font-light">
            <TextWithRegisteredSymbol>{heading}</TextWithRegisteredSymbol>
          </h2>
          <div className="laptop:block flex justify-center">
            <CardSlider data={sliderData} />
          </div>
        </div>
      </section>
    </>
  );
};
